import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Droplets, Sprout, PawPrint, HeartHandshake } from "lucide-react";
import { useReveal } from "../pages/Landing";
import { PublicHeader, ExploreDemo } from "./PublicHeader";
import { PublicFooter } from "./PublicFooter";

export const IMG = (n) => `/img/b8/${n}.webp`;
export const DIRS = {
  A: { icon: Droplets, card: "bg-sage border-sage-line", ink: "text-sage-ink", dot: "bg-sage-strong" },
  B: { icon: Sprout, card: "bg-peach border-peach-line", ink: "text-clay-strong", dot: "bg-clay" },
  C: { icon: PawPrint, card: "bg-lavender border-lavender-line", ink: "text-lavender-ink", dot: "bg-lavender-ink" },
  D: { icon: HeartHandshake, card: "bg-amber-soft border-amber-line", ink: "text-amber-ink", dot: "bg-amber-ink" },
};

export function EdShell({ testid, children }) {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return undefined; }
    let n = 0, t;
    const go = () => {
      const el = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (el) el.scrollIntoView({ block: "start", behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
      else if (n++ < 40) t = setTimeout(go, 75);
    };
    go();
    return () => clearTimeout(t);
  }, [pathname, hash]);
  return <div className="min-h-screen grain overflow-x-hidden" data-testid={testid}><PublicHeader /><main className="mx-auto max-w-6xl px-4 pb-6 sm:px-6">{children}</main><PublicFooter /></div>;
}

export function Reveal({ as: Tag = "section", className = "", children, ...p }) {
  const ref = useReveal();
  return <Tag ref={ref} className={className} {...p}>{children}</Tag>;
}

export function Band({ eyebrow, title, testid, children, id }) {
  return (
    <Reveal id={id} data-testid={testid} className="scroll-mt-24 border-t border-sand-line py-12 sm:py-16">
      {eyebrow && <p className="eyebrow flex items-center gap-2"><span className="rv-num">{eyebrow}</span><span className="rv-line h-px w-8 bg-clay/40" aria-hidden /></p>}
      {title && <h2 className="rv-rise mt-3 max-w-3xl font-display text-2xl font-semibold text-ink sm:text-3xl">{title}</h2>}
      <div className="mt-8 min-w-0">{children}</div>
    </Reveal>
  );
}

export function Cta({ to, testid, children, primary }) {
  return <Link to={to} data-testid={testid} className={`btn btn-lg ${primary ? "btn-ink" : "btn-line"} hover:-translate-y-0.5`}>{children}{primary && <ArrowRight className="h-4 w-4" aria-hidden />}</Link>;
}

export function Hero({ eyebrow, title, sub, lead, img, alt, ctas, testid, media, extra }) {
  return (
    <section className="grid items-center gap-8 pb-10 pt-4 lg:grid-cols-12 lg:gap-12 lg:pb-14 lg:pt-12" data-testid={testid}>
      <div className="fade-up min-w-0 lg:col-span-7">
        <p className="eyebrow">{eyebrow}</p>
        <h1 className="mt-3 break-words font-display text-4xl font-semibold text-ink sm:text-5xl lg:text-6xl" data-testid={`${testid}-title`}>{title}</h1>
        {sub && <div className="mt-4 font-display text-lg italic text-clay-strong md:text-xl" data-testid={`${testid}-sub`}>{sub}</div>}
        {lead && <p className="mt-5 max-w-2xl text-base text-neutral-ink md:text-lg" data-testid={`${testid}-lead`}>{lead}</p>}
        <div className="mt-7 flex flex-wrap gap-3">{ctas}</div>
        {extra}
      </div>
      <div className="fade-up-2 order-first min-w-0 lg:order-none lg:col-span-5">
        {media || <img src={img} alt={alt} fetchPriority="high" width="1200" height="800" className="aspect-[16/9] w-full rounded-[28px] border border-sand-line object-cover shadow-card lg:aspect-[4/3]" data-testid={`${testid}-img`} />}
      </div>
    </section>
  );
}

export function CtaBand({ testid = "cta-band" }) {
  const { t } = useTranslation();
  return (
    <Reveal className="my-12 sm:my-16" data-testid={testid}>
      <div className="rv-rise grid overflow-hidden rounded-[28px] border border-peach-line bg-peach md:grid-cols-5">
        <div className="min-w-0 p-6 sm:p-10 md:col-span-3">
          <h2 className="font-display text-2xl font-semibold text-ink sm:text-3xl">{t("b8.cta_title")}</h2>
          <p className="mt-3 max-w-xl text-neutral-ink">{t("b8.cta_sub")}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/register" data-testid={`${testid}-start`} className="btn btn-lg btn-line hover:-translate-y-0.5">{t("b8.start_learning")}</Link>
            <ExploreDemo testid={`${testid}-demo`} className="max-sm:whitespace-normal" />
          </div>
        </div>
        <img src={IMG("cta")} alt={t("b8.cta_alt")} loading="lazy" decoding="async" width="1200" height="800" className="h-48 w-full object-cover md:col-span-2 md:h-full" />
      </div>
    </Reveal>
  );
}

export function DirChip({ k, to, testid }) {
  const { t } = useTranslation();
  const d = DIRS[k] || DIRS.A;
  const Icon = d.icon;
  const cls = `inline-flex min-h-[36px] items-center gap-1.5 rounded-full border px-3 text-sm font-semibold ${d.card} ${d.ink}`;
  const inner = <><Icon className="h-4 w-4" aria-hidden />{t("b8.direction", { k })}</>;
  return to ? <Link to={to} className={`${cls} transition-transform hover:-translate-y-0.5`} data-testid={testid}>{inner}</Link> : <span className={cls} data-testid={testid}>{inner}</span>;
}
